import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Sidebar from '@/components/Sidebar';
import DigitalTabs from '@/components/DigitalTabs';
import {
  Menu, Plus, Trash2, FileText, KeyRound, UploadCloud, Loader2, AlertTriangle, Download
} from 'lucide-react';

const CHUNK_SIZE = 2 * 1024 * 1024; // 2 MB per potongan
const MAX_BYTES = 25 * 1024 * 1024;

const formatSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const randomId = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

export default function DigitalPage() {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'codes' ? 'codes' : 'files';

  const [assets, setAssets] = useState([]);
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const [poolDialogOpen, setPoolDialogOpen] = useState(false);
  const [poolName, setPoolName] = useState('');
  const [poolCodes, setPoolCodes] = useState('');
  const [savingPool, setSavingPool] = useState(false);

  const [importPool, setImportPool] = useState(null);
  const [importCodes, setImportCodes] = useState('');
  const [importing, setImporting] = useState(false);

  const fetchAll = async () => {
    try {
      const [a, p] = await Promise.all([
        axios.get(`${API}/digital/assets`, { headers }),
        axios.get(`${API}/digital/code-pools`, { headers }),
      ]);
      setAssets(a.data.assets || []);
      setPools(p.data.pools || []);
    } catch (error) {
      toast.error('Gagal memuat data produk digital');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error('Ukuran file maksimal 25 MB');
      return;
    }
    setUploading(true);
    setProgress(0);
    const uploadId = randomId() + randomId();
    const total = Math.ceil(file.size / CHUNK_SIZE);
    try {
      for (let i = 0; i < total; i++) {
        const form = new FormData();
        form.append('upload_id', uploadId);
        form.append('chunk_index', i);
        form.append('total_chunks', total);
        form.append('chunk', file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
        await axios.post(`${API}/digital/assets/upload-chunk`, form, { headers });
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      await axios.post(`${API}/digital/assets/upload-complete`,
        { upload_id: uploadId, file_name: file.name }, { headers });
      toast.success('File berhasil diupload');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload gagal, coba lagi');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDeleteAsset = async (asset) => {
    if (!window.confirm(`Hapus file "${asset.file_name}"? Tindakan ini tidak bisa dibatalkan.`)) return;
    try {
      await axios.delete(`${API}/digital/assets/${asset.id}`, { headers });
      toast.success('File dihapus');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus file');
    }
  };

  const handleCreatePool = async () => {
    if (!poolName.trim()) { toast.error('Nama stok kode wajib diisi'); return; }
    setSavingPool(true);
    try {
      const res = await axios.post(`${API}/digital/code-pools`,
        { name: poolName, codes: poolCodes }, { headers });
      toast.success(`Stok kode dibuat (${res.data.added} kode)`);
      setPoolDialogOpen(false);
      setPoolName(''); setPoolCodes('');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal membuat stok kode');
    } finally {
      setSavingPool(false);
    }
  };

  const handleImportCodes = async () => {
    setImporting(true);
    try {
      const res = await axios.post(`${API}/digital/code-pools/${importPool.id}/import`,
        { codes: importCodes }, { headers });
      toast.success(`${res.data.added} kode ditambahkan`);
      setImportPool(null); setImportCodes('');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menambah kode');
    } finally {
      setImporting(false);
    }
  };

  const handleDeletePool = async (pool) => {
    if (!window.confirm(`Hapus stok kode "${pool.name}" beserta semua kodenya?`)) return;
    try {
      await axios.delete(`${API}/digital/code-pools/${pool.id}`, { headers });
      toast.success('Stok kode dihapus');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus stok kode');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A] flex">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <div className="flex-1 lg:ml-64">
        <header className="sticky top-0 backdrop-blur-xl bg-white/85 dark:bg-[#0A0A0A]/85 border-b border-slate-200 dark:border-zinc-800/80 z-30">
          <div className="px-3 sm:px-6 py-2.5 sm:py-4 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800" data-testid="sidebar-toggle">
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50" style={{ fontFamily: 'Manrope' }}>
                  Produk Digital
                </h1>
                <p className="text-xs text-slate-500 dark:text-zinc-400 hidden sm:block">File & stok kode yang dikirim otomatis ke pembeli</p>
              </div>
            </div>
          </div>
          <div className="px-3 sm:px-6 pb-2.5 sm:pb-3">
            <DigitalTabs active={tab === 'codes' ? 'codes' : 'files'} />
          </div>
        </header>

        <main className="p-3 sm:p-6 space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 dark:text-zinc-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Memuat...
            </div>
          ) : tab === 'files' ? (
            <>
              <div className="rounded-2xl border border-dashed border-slate-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-6 text-center">
                <input ref={fileInputRef} type="file" className="hidden" onChange={handleUpload} data-testid="digital-asset-upload-input" />
                <UploadCloud className="w-8 h-8 mx-auto text-indigo-500 mb-2" />
                <p className="text-sm font-medium text-slate-800 dark:text-zinc-100">Upload file produk digital</p>
                <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1">
                  PDF, ZIP, e-book, template. Maksimal 25 MB. File disimpan privat dan hanya bisa diunduh lewat link berbatas.
                </p>
                {uploading ? (
                  <div className="mt-3">
                    <div className="h-1.5 w-full max-w-xs mx-auto bg-slate-200 dark:bg-zinc-800 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-600 transition-all" style={{ width: `${progress}%` }} />
                    </div>
                    <p className="text-xs text-slate-500 mt-1.5" data-testid="upload-progress">Mengupload {progress}%</p>
                  </div>
                ) : (
                  <Button className="mt-3 bg-indigo-600 hover:bg-indigo-700 h-9 text-sm" onClick={() => fileInputRef.current?.click()} data-testid="upload-asset-btn">
                    <Plus className="w-4 h-4 mr-1.5" /> Pilih File
                  </Button>
                )}
              </div>

              {assets.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-zinc-400 text-center py-6" data-testid="assets-empty">Belum ada file.</p>
              ) : (
                <div className="space-y-2">
                  {assets.map((a) => (
                    <div key={a.id} className="rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-3 flex items-center gap-3" data-testid={`asset-row-${a.id}`}>
                      <FileText className="w-5 h-5 text-indigo-500 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 dark:text-zinc-50 truncate">{a.file_name}</p>
                        <p className="text-xs text-slate-500 dark:text-zinc-400">
                          {formatSize(a.size_bytes)}
                          {a.used_by?.length ? ` · dipakai: ${a.used_by.join(', ')}` : ' · belum dipakai produk'}
                        </p>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteAsset(a)} className="text-red-600 hover:text-red-700 h-8 w-8 p-0" data-testid={`delete-asset-${a.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex justify-end">
                <Button className="bg-indigo-600 hover:bg-indigo-700 h-9 text-sm" onClick={() => setPoolDialogOpen(true)} data-testid="add-code-pool-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Stok Kode Baru
                </Button>
              </div>

              {pools.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-zinc-400 text-center py-6" data-testid="pools-empty">
                  Belum ada stok kode. Buat stok lalu tempel daftar kode dari Excel.
                </p>
              ) : (
                <div className="space-y-2">
                  {pools.map((p) => (
                    <div key={p.id} className="rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-3" data-testid={`pool-row-${p.id}`}>
                      <div className="flex items-center gap-3">
                        <KeyRound className="w-5 h-5 text-indigo-500 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-900 dark:text-zinc-50 truncate">{p.name}</p>
                          <p className="text-xs text-slate-500 dark:text-zinc-400">
                            Sisa {p.available} dari {p.total} kode · terpakai {p.used}
                          </p>
                        </div>
                        {p.available <= 5 && (
                          <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 text-[10px] gap-1" data-testid={`pool-low-${p.id}`}>
                            <AlertTriangle className="w-3 h-3" /> Hampir habis
                          </Badge>
                        )}
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { setImportPool(p); setImportCodes(''); }} data-testid={`import-codes-${p.id}`}>
                          Tambah Kode
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeletePool(p)} className="text-red-600 hover:text-red-700 h-8 w-8 p-0" data-testid={`delete-pool-${p.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </main>
      </div>

      <Dialog open={poolDialogOpen} onOpenChange={setPoolDialogOpen}>
        <DialogContent className="sm:max-w-[480px] bg-white dark:bg-zinc-900">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-bold">
              <KeyRound className="w-5 h-5 text-indigo-600" /> Stok Kode Baru
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nama stok</Label>
              <Input value={poolName} onChange={(e) => setPoolName(e.target.value)} placeholder="Lisensi Tahunan 2026" className="mt-1" data-testid="pool-name-input" />
            </div>
            <div>
              <Label>Daftar kode (satu per baris)</Label>
              <Textarea value={poolCodes} onChange={(e) => setPoolCodes(e.target.value)} rows={7}
                placeholder={"ABCD-1234-EFGH\nIJKL-5678-MNOP"} className="mt-1 font-mono text-xs" data-testid="pool-codes-input" />
              <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1">Kode disimpan terenkripsi. Kode kembar otomatis dilewati.</p>
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1" onClick={() => setPoolDialogOpen(false)}>Batal</Button>
              <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700" onClick={handleCreatePool} disabled={savingPool} data-testid="save-pool-btn">
                {savingPool ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Simpan'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!importPool} onOpenChange={(v) => !v && setImportPool(null)}>
        <DialogContent className="sm:max-w-[480px] bg-white dark:bg-zinc-900">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Tambah Kode - {importPool?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea value={importCodes} onChange={(e) => setImportCodes(e.target.value)} rows={8}
              placeholder={"ABCD-1234-EFGH\nIJKL-5678-MNOP"} className="font-mono text-xs" data-testid="import-codes-input" />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setImportPool(null)}>Batal</Button>
              <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700" onClick={handleImportCodes} disabled={importing} data-testid="save-import-codes-btn">
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Tambahkan'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
